/**
 * Tiered Provider Sets — admin page for the tiered-set registry.
 *
 * Each set is a named ordered list of tier slots (weakest at index 0,
 * strongest at the last index) used by analysis tier routing. See
 * docs/FeatureDesigns/PipelineInfra/analysis-routing.md.
 *
 * Tier slots are pure references — either a provider key (kind: 'single') or
 * a parallel-set name (kind: 'parallel'). LLM parameters (temperature,
 * maxTokens, reasoningEffort, thinkingBudgetTokens, softCostCapUsd) live on
 * the provider's registry entry; the editor here just picks the slot shape
 * and the referenced key/name.
 *
 * The backend supports any number of tiers per set. **MAX_TIERS below is the
 * only place a maximum is enforced** — it is purely a UX cap on the editor.
 */

import { useMemo, useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { adminApi } from '../../api/adminApi';
import type { ProviderConfig, ProviderTieredSet, TierSlot } from '../../types/settings';
import { DataTable, type ColumnDef, Modal, Button, Input, Select } from '@shared-ui/components';
import { useProviderSetUsage, UsedByCell } from './UsedByCell';
import { SlotParamsFields } from './SlotParamsFields';
import './ProviderTieredSets.css';

/** UX cap on tiers per set. The backend imposes no upper bound. */
const MAX_TIERS = 5;
const MIN_TIERS = 1;

function emptySlot(): TierSlot {
  return { kind: 'single', provider: '' };
}

function emptySet(): ProviderTieredSet {
  return {
    name: '',
    description: '',
    levels: [emptySlot(), emptySlot(), emptySlot()],
  };
}

function tierLabel(index: number): string {
  return `Tier ${index + 1}`;
}

/**
 * Comparable shape for the dirty check. Fixed key order, and absent/empty
 * collapsed to '' — a stored set may omit `description` entirely while the
 * textarea writes '', which is not an edit the operator made.
 */
function normalizeForCompare(set?: ProviderTieredSet) {
  if (!set) return null;
  return {
    name: set.name ?? '',
    description: set.description ?? '',
    levels: (set.levels ?? []).map(slot => (
      slot?.kind === 'parallel'
        ? { kind: 'parallel', ref: slot.parallelSet ?? '' }
        // params included: editing only a slot's budget must register as dirty,
        // otherwise Save stays disabled and the change is silently lost.
        : { kind: 'single', ref: slot?.provider ?? '', params: slot?.params ?? null }
    )),
  };
}

export function ProviderTieredSets() {
  const queryClient = useQueryClient();
  const [editing, setEditing] = useState<{ mode: 'create' | 'edit'; original?: string; draft: ProviderTieredSet } | null>(null);
  const [error, setError] = useState<string | null>(null);

  const { data: setsData, isLoading } = useQuery({
    queryKey: ['provider-tiered-sets'],
    queryFn: () => adminApi.listProviderTieredSets(),
  });
  const { data: providersData } = useQuery({
    queryKey: ['providers'],
    queryFn: () => adminApi.getProviderConfig(),
  });
  const { data: parallelSetsData } = useQuery({
    queryKey: ['provider-parallel-sets'],
    queryFn: () => adminApi.listProviderParallelSets(),
  });

  const sets = setsData?.sets ?? [];
  const providers: ProviderConfig[] = providersData?.providers ?? [];
  const activeProviderKeys = providers.filter(p => p.status !== 'deprecated').map(p => p.key);
  const parallelSetNames = (parallelSetsData?.sets ?? []).map(s => s.name);

  // Which pipelines route through each set — replaces the description column,
  // which repeated what the tier cells already show.
  const usage = useProviderSetUsage(sets);

  const createMut = useMutation({
    mutationFn: (set: ProviderTieredSet) => adminApi.createProviderTieredSet(set),
    onSuccess: () => { queryClient.invalidateQueries({ queryKey: ['provider-tiered-sets'] }); setEditing(null); setError(null); },
    onError: (err: Error) => setError(err.message),
  });
  const updateMut = useMutation({
    mutationFn: ({ name, set }: { name: string; set: ProviderTieredSet }) => adminApi.updateProviderTieredSet(name, set),
    onSuccess: () => { queryClient.invalidateQueries({ queryKey: ['provider-tiered-sets'] }); setEditing(null); setError(null); },
    onError: (err: Error) => setError(err.message),
  });
  const deleteMut = useMutation({
    mutationFn: (name: string) => adminApi.deleteProviderTieredSet(name),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['provider-tiered-sets'] }),
    onError: (err: Error) => setError(err.message),
  });

  function handleSave() {
    if (!editing) return;
    setError(null);
    if (editing.mode === 'create') {
      createMut.mutate(editing.draft);
    } else {
      updateMut.mutate({ name: editing.original!, set: editing.draft });
    }
  }

  function handleDelete(name: string) {
    if (!confirm(`Delete tiered provider set "${name}"? Pipeline cells that reference it will start failing.`)) return;
    deleteMut.mutate(name);
  }

  function renderSlotCell(slot: TierSlot | undefined) {
    if (!slot) return <span style={{ color: 'var(--text-tertiary)' }}>—</span>;
    if (slot.kind === 'parallel') {
      return (
        <div className="provider-sets-level">
          <span className="provider-sets-provider">parallel → {slot.parallelSet}</span>
        </div>
      );
    }
    return (
      <div className="provider-sets-level">
        <span className="provider-sets-provider">{slot.provider || '—'}</span>
      </div>
    );
  }

  /** Count of tiers shown in the table. Picks the largest set so the column
   *  count is stable across rows; rows with fewer tiers render an em-dash. */
  const tierColumnCount = useMemo(
    () => sets.reduce((max, s) => Math.max(max, s.levels?.length ?? 0), 3),
    [sets],
  );

  const columns: ColumnDef<ProviderTieredSet>[] = useMemo(() => {
    const tierCols: ColumnDef<ProviderTieredSet>[] = Array.from({ length: tierColumnCount }, (_, i) => ({
      key: `tier-${i}`,
      header: tierLabel(i),
      render: (s: ProviderTieredSet) => renderSlotCell(s.levels?.[i]),
    }));
    return [
      { key: 'name', header: 'Name', render: (s) => <strong>{s.name}</strong> },
      ...tierCols,
      {
        key: 'usedBy',
        header: 'Used by',
        className: 'provider-sets-usedby',
        render: (s) => <UsedByCell usages={usage.tiered.get(s.name)} loading={usage.loading} />,
      },
      {
        key: 'actions',
        header: '',
        render: (s) => (
          <div className="provider-sets-row-actions">
            <Button
              variant="danger"
              size="sm"
              onClick={(e) => { e.stopPropagation(); handleDelete(s.name); }}
              disabled={deleteMut.isPending}
            >
              Delete
            </Button>
          </div>
        ),
      },
    ];
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [deleteMut.isPending, tierColumnCount, usage.tiered, usage.loading]);

  function writeSlot(index: number, nextSlot: TierSlot) {
    if (!editing) return;
    const nextLevels = [...editing.draft.levels];
    nextLevels[index] = nextSlot;
    setEditing({ ...editing, draft: { ...editing.draft, levels: nextLevels } });
  }

  function addTier() {
    if (!editing) return;
    if (editing.draft.levels.length >= MAX_TIERS) return;
    setEditing({
      ...editing,
      draft: { ...editing.draft, levels: [...editing.draft.levels, emptySlot()] },
    });
  }

  /**
   * Swap a tier with its neighbour. Reordering matters here beyond taste: the
   * index IS the escalation order (weakest at Tier 1), so moving a provider up
   * or down changes when it gets used. Without this, correcting the order of a
   * four-tier set meant re-picking every dropdown.
   */
  function moveTier(index: number, direction: -1 | 1) {
    if (!editing) return;
    const target = index + direction;
    if (target < 0 || target >= editing.draft.levels.length) return;
    const nextLevels = [...editing.draft.levels];
    [nextLevels[index], nextLevels[target]] = [nextLevels[target], nextLevels[index]];
    setEditing({ ...editing, draft: { ...editing.draft, levels: nextLevels } });
  }

  function removeTier(index: number) {
    if (!editing) return;
    if (editing.draft.levels.length <= MIN_TIERS) return;
    const nextLevels = editing.draft.levels.filter((_, i) => i !== index);
    setEditing({ ...editing, draft: { ...editing.draft, levels: nextLevels } });
  }

  // Single select per tier with prefixed option values — avoids a separate
  // radio control. `provider:<key>` → single slot, `parallel:<name>` → parallel slot.
  const PROVIDER_PREFIX = 'provider:';
  const PARALLEL_PREFIX = 'parallel:';

  function slotToValue(slot: TierSlot): string {
    if (slot.kind === 'single') return slot.provider ? `${PROVIDER_PREFIX}${slot.provider}` : '';
    return slot.parallelSet ? `${PARALLEL_PREFIX}${slot.parallelSet}` : '';
  }

  function valueToSlot(val: string): TierSlot {
    if (val.startsWith(PROVIDER_PREFIX)) return { kind: 'single', provider: val.slice(PROVIDER_PREFIX.length) };
    if (val.startsWith(PARALLEL_PREFIX)) return { kind: 'parallel', parallelSet: val.slice(PARALLEL_PREFIX.length) };
    return { kind: 'single', provider: '' };
  }

  function handleCancel() {
    if (!editing) return;
    // Compare against what the modal opened with, not against emptiness — an
    // existing set is never empty, so the old check prompted on every cancel
    // and trained the operator to dismiss it.
    const baseline = editing.mode === 'edit'
      ? sets.find(s => s.name === editing.original)
      : emptySet();
    const dirty = JSON.stringify(normalizeForCompare(editing.draft)) !== JSON.stringify(normalizeForCompare(baseline));
    if (dirty && !confirm('Discard changes?')) return;
    setEditing(null);
  }

  if (isLoading) return <div className="provider-sets-loading">Loading tiered provider sets…</div>;

  return (
    <div className="provider-sets-page">
      <div className="provider-sets-header">
        <div>
          <h2>Tiered Provider Sets</h2>
          <p className="provider-sets-subtitle">
            Named ordered tier lists (Tier 1 = cheap → last tier = premium) used by analysis tier routing. Pipeline cells reference a set by name. Up to {MAX_TIERS} tiers per set.
          </p>
        </div>
        <Button variant="primary" size="sm" onClick={() => setEditing({ mode: 'create', draft: emptySet() })}>+ New Set</Button>
      </div>

      {error && <div className="provider-sets-error">{error}</div>}

      <DataTable
        columns={columns}
        data={sets}
        rowKey={(s) => s.name}
        onRowClick={(s) => setEditing({ mode: 'edit', original: s.name, draft: structuredClone(s) })}
        loading={isLoading}
        emptyMessage='No tiered provider sets yet. Click "+ New Set" to create one.'
        compact
        bordered
      />

      {editing && (
        <Modal
          isOpen={true}
          onClose={handleCancel}
          title={editing.mode === 'create' ? 'New Tiered Provider Set' : `Edit "${editing.original}"`}
          size="lg"
          closeOnOverlayClick={false}
          closeOnEscape={false}
        >
          <div className="provider-sets-form">
            <label>
              Name
              <Input
                type="text"
                value={editing.draft.name}
                placeholder="e.g. anthropic-standard"
                onChange={e => setEditing({ ...editing, draft: { ...editing.draft, name: e.target.value } })}
                disabled={editing.mode === 'edit'}
              />
            </label>
            <label>
              Description
              <textarea
                rows={2}
                value={editing.draft.description ?? ''}
                onChange={e => setEditing({ ...editing, draft: { ...editing.draft, description: e.target.value } })}
              />
            </label>

            {editing.draft.levels.map((slot, index) => {
              const canRemove = editing.draft.levels.length > MIN_TIERS;
              const canMoveUp = index > 0;
              const canMoveDown = index < editing.draft.levels.length - 1;
              return (
                <div key={index} className="provider-sets-tier-picker">
                  {/* Controls stay on one line; the per-provider params sit
                      beneath them. Keeping the params inside the same grid row
                      meant a fifth child in a four-column grid, which pushed
                      Remove onto its own line and starved the model select. */}
                  <div className="provider-sets-tier-controls">
                    <span className="provider-sets-tier-picker-label">{tierLabel(index)}</span>
                    <Select
                      value={slotToValue(slot)}
                      onChange={e => writeSlot(index, valueToSlot(e.target.value))}
                    >
                      <option value="">— pick —</option>
                      <optgroup label="Providers">
                        {activeProviderKeys.map(k => (
                          <option key={k} value={`${PROVIDER_PREFIX}${k}`}>{k}</option>
                        ))}
                      </optgroup>
                      {parallelSetNames.length > 0 && (
                        <optgroup label="Parallel sets">
                          {parallelSetNames.map(n => (
                            <option key={n} value={`${PARALLEL_PREFIX}${n}`}>{n} (parallel)</option>
                          ))}
                        </optgroup>
                      )}
                    </Select>
                    <div className="provider-sets-tier-reorder">
                      <button
                        type="button"
                        className="provider-sets-tier-move"
                        onClick={() => moveTier(index, -1)}
                        disabled={!canMoveUp}
                        aria-label={`Move ${tierLabel(index)} up`}
                        title={canMoveUp ? `Swap with ${tierLabel(index - 1)}` : 'Already the first tier'}
                      >
                        ▲
                      </button>
                      <button
                        type="button"
                        className="provider-sets-tier-move"
                        onClick={() => moveTier(index, 1)}
                        disabled={!canMoveDown}
                        aria-label={`Move ${tierLabel(index)} down`}
                        title={canMoveDown ? `Swap with ${tierLabel(index + 1)}` : 'Already the last tier'}
                      >
                        ▼
                      </button>
                    </div>
                    <Button
                      type="button"
                      variant="danger"
                      size="sm"
                      onClick={() => removeTier(index)}
                      disabled={!canRemove}
                      title={canRemove ? 'Remove this tier' : `A set must keep at least ${MIN_TIERS} tier`}
                    >
                      Remove
                    </Button>
                  </div>
                  {slot.kind === 'single' && slot.provider && (
                    <SlotParamsFields
                      provider={providers.find(p => p.key === slot.provider)}
                      params={slot.params}
                      onChange={next => writeSlot(index, {
                        kind: 'single',
                        provider: slot.provider,
                        ...(next ? { params: next } : {}),
                      })}
                      disabled={createMut.isPending || updateMut.isPending}
                    />
                  )}
                </div>
              );
            })}

            <div className="provider-sets-add-tier-row">
              {/* Disabled buttons don't fire mouseover, so a `title` directly
                  on the button is invisible. Wrap in a span that gets the
                  tooltip — pointer events still reach the wrapper. */}
              <span
                title={
                  editing.draft.levels.length >= MAX_TIERS
                    ? `Max ${MAX_TIERS} tiers are allowed`
                    : 'Add another tier above this one'
                }
              >
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  onClick={addTier}
                  disabled={editing.draft.levels.length >= MAX_TIERS}
                >
                  + Add Tier
                </Button>
              </span>
            </div>

            <div className="provider-sets-form-actions">
              <Button variant="secondary" size="sm" onClick={handleCancel}>Cancel</Button>
              <Button variant="primary" size="sm" onClick={handleSave} disabled={createMut.isPending || updateMut.isPending}>
                {createMut.isPending || updateMut.isPending ? 'Saving…' : 'Save'}
              </Button>
            </div>
          </div>
        </Modal>
      )}
    </div>
  );
}
